import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatUsDate } from '../util/formatters';
import { CollapsibleCard } from './CollapsibleCard';
import { InfoField } from './InfoField';
// Deep link into the Apaleo web app. Property code is the URL slug
// (e.g. `BWI1`), so we prefer that over the numeric propertyId for the path.
const apaleoReservationUrl = (propertyCode, reservationId) => {
    if (!propertyCode || !reservationId) {
        return undefined;
    }
    return `https://app.apaleo.com/${encodeURIComponent(propertyCode)}/reservations/${encodeURIComponent(reservationId)}`;
};
// Apaleo returns localized names as `{en: '...'}` objects; older records or
// simpler resources sometimes carry the plain string. Accept either.
const localizedName = (value) => {
    if (typeof value === 'string') {
        return value;
    }
    if (value && typeof value === 'object' && 'en' in value) {
        const en = value.en;
        return typeof en === 'string' ? en : undefined;
    }
    return undefined;
};
const readString = (container, key) => {
    const value = container?.[key];
    return typeof value === 'string' ? value : undefined;
};
const readRecord = (container, key) => {
    const value = container?.[key];
    return value && typeof value === 'object'
        ? value
        : undefined;
};
export const ApaleoStayCard = ({ caseData }) => {
    const details = caseData.apaleoDetails;
    const reservation = readRecord(details, 'reservation');
    const property = readRecord(details, 'property');
    const ratePlan = readRecord(details, 'ratePlan');
    const cancellationPolicy = readRecord(details, 'cancellationPolicy');
    const embeddedCancellationPolicy = readRecord(ratePlan, 'cancellationPolicy');
    const reservationProperty = readRecord(reservation, 'property');
    const embeddedRatePlan = readRecord(reservation, 'ratePlan');
    const reservationId = readString(reservation, 'id') ?? caseData.reservationId;
    const propertyId = readString(property, 'id') ??
        readString(reservationProperty, 'id') ??
        caseData.propertyId;
    const propertyCode = readString(property, 'code') ?? readString(reservationProperty, 'code');
    const arrival = readString(reservation, 'arrival');
    const departure = readString(reservation, 'departure');
    const ratePlanName = localizedName(ratePlan?.name) ?? localizedName(embeddedRatePlan?.name);
    const ratePlanCode = readString(ratePlan, 'code') ?? readString(embeddedRatePlan, 'code');
    const ratePlanDisplay = ratePlanCode && ratePlanName
        ? `${ratePlanCode} - ${ratePlanName}`
        : ratePlanCode ?? ratePlanName;
    const cancellationPolicyCode = readString(cancellationPolicy, 'code') ??
        readString(embeddedCancellationPolicy, 'code');
    const cancellationPolicyDescription = readString(cancellationPolicy, 'description') ??
        readString(embeddedCancellationPolicy, 'description');
    const cancellationPolicyDisplay = cancellationPolicyCode && cancellationPolicyDescription
        ? `${cancellationPolicyCode} - ${cancellationPolicyDescription}`
        : cancellationPolicyCode ?? cancellationPolicyDescription;
    const comment = readString(reservation, 'comment');
    const guestComment = readString(reservation, 'guestComment');
    const reservationUrl = apaleoReservationUrl(propertyCode, reservationId);
    return (_jsx(CollapsibleCard, { title: "Stay Details", children: !details && !reservationId ? (_jsx("p", { className: "text-sm opacity-70 italic", children: "No reservation matched to this chargeback." })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "grid grid-cols-2 gap-3", children: [_jsx(InfoField, { label: "Property ID", value: propertyId, mono: true }), _jsx(InfoField, { label: "Reservation ID", value: reservationId, mono: true }), _jsx(InfoField, { label: "Check-in", value: formatUsDate(arrival) }), _jsx(InfoField, { label: "Check-out", value: formatUsDate(departure) }), _jsx("div", { className: "col-span-2", children: _jsx(InfoField, { label: "Rate plan", value: ratePlanDisplay }) }), _jsx("div", { className: "col-span-2", children: _jsx(InfoField, { label: "Cancellation policy", value: cancellationPolicyDisplay }) }), guestComment ? (_jsx("div", { className: "col-span-2", children: _jsx(InfoField, { label: "Guest Comment", value: guestComment }) })) : null, comment ? (_jsx("div", { className: "col-span-2", children: _jsx(InfoField, { label: "Reservation Comment", value: comment }) })) : null] }), reservationUrl ? (_jsxs("a", { href: reservationUrl, target: "_blank", rel: "noopener noreferrer", className: "link link-primary text-sm inline-flex items-center gap-1", children: ["Reservation in Apaleo", _jsx(FontAwesomeIcon, { icon: faArrowUpRightFromSquare })] })) : null] })) }));
};
